import schoolAxiosInstance from "@quest/school-axios";
import { superAdminAxiosInstance } from "@quest/superadmin-axios";
import { AddMembers } from "../schema/add-members.schema";
import {
  DenialsPayload,
  IMember,
  IUnenrolledMentor,
  IUserResponse,
  Member,
  MentorRemovalImpact,
} from "../types";
import { BaseGetRequestParams, BaseGetRequestResponse } from "@quest/types";
import { RolePermissions } from "../types/user-permissions";

export interface IStudent {
  id: number;
  userId: string;
  fullName: string;
  studentId: string;
  studentEmail: string;
  gender: string;
  country: string;
  nationality: string;
  educationLevel: string;
  dateOfBirth: string;
  status: boolean;
  schoolYear: string;
  class: string;
  profile_image: string;

  idpId: string;
}

class AdminService {
  private api = superAdminAxiosInstance;
  private schoolApi = schoolAxiosInstance;

  async addTenantMember(data: AddMembers) {
    const response = await this.api.post(`/users/create-school-admin`, data);
    return response.data;
  }

  async createMentor(data: AddMembers, isSchool: boolean) {
    let response;
    if (isSchool) {
      response = await this.schoolApi.post(`/users/mentors`, data);
    } else {
      response = await this.api.post(`/users/create-mentor-user`, data);
    }
    return response.data;
  }

  async createParents(data: AddMembers) {
    const response = await this.schoolApi.post(
      `/users/create-parent-user`,
      data,
    );
    return response.data;
  }

  async getTenantMembers(
    tenantSlug: string,
    isSchool: boolean,
    params: BaseGetRequestParams,
  ) {
    let response;

    if (isSchool) {
      response = await this.schoolApi.get<IMember>(`/users/admin-list`, {
        params,
      });
    } else {
      response = await this.api.get<IMember>(`/users/admin-list`, {
        params,
        headers: {
          "x-tenant-name": tenantSlug,
        },
      });
    }
    return response.data.data;
  }

  async getEnrolledMentors(params: BaseGetRequestParams) {
    const response = await this.schoolApi.get<BaseGetRequestResponse<Member>>(
      `/users/mentor-list`,
      {
        params,
      },
    );
    return response.data.data;
  }

  async getUnenrolledMentors(params: BaseGetRequestParams) {
    const response = await this.schoolApi.get<
      BaseGetRequestResponse<IUnenrolledMentor>
    >(`/users/mentors`, {
      params,
    });
    return response.data.data;
  }

  async getIndividualMember(
    userId: string,
    tenantSlug: string,
    isSchool: boolean,
  ) {
    let response;

    if (isSchool) {
      response = await this.schoolApi.get<IUserResponse>(`/users/${userId}`);
    } else {
      response = await this.api.get<IUserResponse>(`/users/${userId}`, {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      });
    }
    return response.data;
  }

  async deactivateMember(
    userId: string,
    tenantSlug: string,
    isSchool: boolean,
  ) {
    let response;
    if (isSchool) {
      response = await this.schoolApi.patch(
        `/users/deactivate-mentor-user/${userId}`,
      );
    } else {
      response = await this.api.delete(`/users/${userId}`, {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      });
    }
    return response.data;
  }

  async getMentorRemovalImpact(
    userId: string,
    tenantSlug: string,
    isSchool: boolean,
  ): Promise<MentorRemovalImpact> {
    if (isSchool) {
      const response = await this.schoolApi.get<{
        data: MentorRemovalImpact;
      }>(`/users/mentor-removal-impact/${userId}`);
      return response.data.data;
    }
    const response = await this.api.get<{ data: MentorRemovalImpact }>(
      `/users/mentor-removal-impact/${userId}`,
      {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      },
    );
    return response.data.data;
  }

  async removeMentor(
    userId: string,
    tenantSlug: string,
    isSchool: boolean,
    transferToUserId?: number | null,
  ) {
    const body =
      transferToUserId != null ? { transferToUserId } : {};

    if (isSchool) {
      const response = await this.schoolApi.patch(
        `/users/deactivate-mentor-user/${userId}`,
        body,
      );
      return response.data;
    }

    const response = await this.api.patch(
      `/users/deactivate-mentor-user/${userId}`,
      body,
      {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      },
    );
    return response.data;
  }

  async getEnrolledMentorsForTenant(
    params: BaseGetRequestParams,
    tenantSlug: string,
    isSchool: boolean,
  ) {
    if (isSchool) {
      return this.getEnrolledMentors(params);
    }
    const response = await this.api.get<BaseGetRequestResponse<Member>>(
      `/users/mentor-list`,
      {
        params,
        headers: {
          "x-tenant-name": tenantSlug,
        },
      },
    );
    return response.data.data;
  }

  async getUnenrolledMentorsForTenant(
    params: BaseGetRequestParams,
    tenantSlug: string,
    isSchool: boolean,
  ) {
    if (isSchool) {
      return this.getUnenrolledMentors(params);
    }
    const response = await this.api.get<
      BaseGetRequestResponse<IUnenrolledMentor>
    >(`/users/mentors`, {
      params,
      headers: {
        "x-tenant-name": tenantSlug,
      },
    });
    return response.data.data;
  }

  async reactivateMentor(mentorId: string) {
    const response = await this.schoolApi.patch(
      `/users/activate-mentor-user/${mentorId}`,
    );
    return response.data;
  }

  async reactivateMember(
    userId: string,
    tenantSlug: string,
    isSchool: boolean,
  ) {
    if (isSchool) {
      const response = await this.schoolApi.patch(
        `/users/${userId}/activate`,
      );
      return response.data;
    }
    const response = await this.api.patch(
      `/users/${userId}/activate`,
      {},
      {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      },
    );
    return response.data;
  }

  async reactivateStudent(studentId: string) {
    const response = await this.schoolApi.put(`/student/activate/${studentId}`);
    return response.data;
  }

  async deactivateParent(parentId: string) {
    const response = await this.schoolApi.patch(
      `/users/deactivate-parent-user/${parentId}`,
    );
    return response.data;
  }

  //
  async reactivateParent(parentId: string) {
    const response = await this.schoolApi.patch(
      `/users/activate-parent-user/${parentId}`,
    );
    return response.data;
  }

  async updateMember(
    userId: string,
    tenantSlug: string,
    data: AddMembers,
    isSchool: boolean,
  ) {
    let response;
    if (isSchool) {
      response = await this.schoolApi.put(
        `/users/update-mentor-user/${userId}`,
        data,
      );
    } else {
      response = await this.api.put(`/users/${userId}`, data, {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      });
      return response.data;
    }
  }

  async resendEmail(
    userUUID: string,
    tenantSlug: string,
    redirectUri: string,
    isSuperadmin: boolean,
  ) {
    let response;
    if (isSuperadmin) {
      response = await this.api.post(`/users/send-password-reset`, {
        userUUID,
        tenantSlug,
        redirectUri,
      });
    } else {
      response = await this.schoolApi.post(`/users/send-password-reset`, {
        userUUID,
        tenantSlug,
        redirectUri,
      });
    }
    return response.data;
  }

  async getStudents(params: BaseGetRequestParams) {
    const response = await this.schoolApi.get<BaseGetRequestResponse<IStudent>>(
      "/student",
      {
        params,
      },
    );

    return response.data.data;
  }

  async getParentStudents(parentId: number) {
    const response = await this.schoolApi.get(
      `/users/parent/${parentId}/student-list`,
    );
    return response.data.data;
  }

  async updateParentStudents(parentId: number, student_ids: number[]) {
    const response = await this.schoolApi.put(
      `/users/parent/${parentId}/update-students-list`,
      { student_ids },
    );
    return response.data.data;
  }

  // bulk import mentors
  async bulkImportMentors(data: FormData) {
    const response = await this.schoolApi.post(`/users/mentors/import`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async getImportList(params: BaseGetRequestParams) {
    const response = await this.schoolApi.get("/users/mentors/imports", {
      params,
    });
    return response.data.data;
  }

  async getImportErrorDetails(importId: string, params: BaseGetRequestParams) {
    const response = await this.schoolApi.get(
      `/users/mentors/import/${importId}/errors`,
      {
        params,
      },
    );
    return response.data.data;
  }

  async enrollIndividualMentor(mentorId: number) {
    const response = await this.schoolApi.post(
      `/users/mentors/invitation/send`,
      {
        mentorId,
      },
    );
    return response.data;
  }

  async enrollMultipleMentors(mentorIds: number[]) {
    const response = await this.schoolApi.post(
      `/users/mentors/invitation/send-bulk`,
      {
        mentorIds,
      },
    );
    return response.data;
  }

  async getUserPermissions() {
    const response = await this.schoolApi.get<RolePermissions>(
      `/tenant/permissions/role/teacher`,
    );

    return response.data.data;
  }

  async getUserDenials(tenantSlug: string) {
    const response = await this.schoolApi.get<
      DenialsPayload & { data?: DenialsPayload }
    >(`/tenant/${tenantSlug}/mentor-permissions`);
    const root = response.data;
    return root.data ?? root;
  }

  /** QUES-1388: school-wide switch for mentor publish/unpublish rights. */
  async updateMentorPublishPermission(
    schoolId: string | number,
    mentors_can_publish_assignments: boolean,
  ) {
    const response = await this.schoolApi.patch(
      `/school/${schoolId}/mentor-publish-permission`,
      { mentors_can_publish_assignments },
    );
    return response.data;
  }

  async updateUserPermissions(payload: DenialsPayload, tenantSlug: string) {
    const response = await this.schoolApi.put(
      `/tenant/${tenantSlug}/mentor-permissions`,
      payload,
    );
    return response.data;
  }

  async addMentorToClass(
    mentorId: number,
    assignments: Array<{ className: string; schoolYear: number }>,
    isSchool = true,
    tenantSlug = "",
  ) {
    if (isSchool) {
      const response = await this.schoolApi.post(
        `/users/mentors/${mentorId}/classes`,
        {
          assignments,
        },
      );
      return response.data;
    }

    const response = await this.api.post(
      `/users/mentors/${mentorId}/classes`,
      {
        assignments,
      },
      {
        headers: {
          "x-tenant-name": tenantSlug,
        },
      },
    );
    return response.data;
  }
}

export const adminService = new AdminService();
